import {
  HStack,
  Icon,
  SkeletonText,
  Stack,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { TitlePageDashboard } from "../../../components/button/TitlePageDashboard";
import { DetailOrderHeader } from "../../../components/card/DetailOrderHeader";
import { ProductTransactionCard } from "../../../components/card/ProductTransactionCard";
import { CardSkeleton } from "../../../components/skeleton/CardSkeleton";
import { StatusInterface } from "../../../constants/OrderStatus";
import { DetailTransactionInterface } from "../../../constants/Transaction";
import useCustomToast from "../../../lib/useCustomToast";
import { useBgBaseColor, useDarkWhiteColor } from "../../../themes/colors";
import formatNumber, {
  getAxiosInstance,
  statusOrderNote,
} from "../../../utils/helperFunction";
import { PrimaryTooltip } from "../../../components/tooltip/PrimaryTooltip";

interface Props extends StackProps {
  pageType: number;
}

export const DetailOrder = ({ pageType, ...rest }: Props) => {
  const { id } = useParams();
  const [data, setData] = useState<DetailTransactionInterface | undefined>(
    undefined
  );

  const [status, setStatus] = useState<StatusInterface>();
  const bg = useDarkWhiteColor();
  const bgBase = useBgBaseColor();
  const [loaded, setLoaded] = useState<boolean>(false);
  const toast = useCustomToast();

  useEffect(() => {
    if (data?.header.status !== undefined) {
      setStatus(statusOrderNote(data.header.status, false));
    }
  }, [data?.header.status]);

  useEffect(() => {
    const axiosInstance = getAxiosInstance(pageType);

    let userType: string = "buyer";

    if (pageType === 2) {
      userType = "reseller";
    } else if (pageType === 3) {
      userType = "supplier";
    }

    axiosInstance
      .get(`/v1/${userType}/purchase/${id}`)
      .then((res) => {
        setData(res.data.data);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [id, pageType]);

  return (
    <VStack
      className="order-detail-page"
      w={"100%"}
      p={4}
      gap={4}
      bg={bgBase}
      align={"stretch"}
      {...rest}
    >
      <Stack direction={["column", "column", "row"]} justify={"space-between"}>
        <TitlePageDashboard isSubPage={true} title="Detail Order" />

        <HStack>
          <HStack
            className="status-order"
            py={1}
            px={2}
            color={status?.color}
            borderColor={status?.color}
          >
            <Icon as={status?.icon} />

            <Text textStyle={"b-reg-sb"} color={status?.color}>
              {data?.header.statusName}
            </Text>
          </HStack>
        </HStack>
      </Stack>

      <DetailOrderHeader
        data={data?.header}
        loaded={loaded}
        pageType={pageType}
      />

      <VStack w={"100%"} p={4} bg={bg} align={"stretch"} borderRadius={"md"}>
        <Text textStyle={"h-reg-title"}>Detail Produk</Text>

        {!loaded ? (
          <CardSkeleton row={1} column={2} height={120} />
        ) : (
          data?.detail.map((item, i) => (
            <ProductTransactionCard
              key={i}
              orderId={data.header.id}
              pageType={pageType}
              data={item}
              footer={data?.footer}
            />
          ))
        )}
      </VStack>

      <VStack w={"100%"} p={4} bg={bg} align={"stretch"} borderRadius={"md"}>
        <Text textStyle={"h-reg-title"}>Rincian Tranksasi</Text>

        {pageType !== 2 && (
          <>
            <HStack justify={"space-between"}>
              <Text textStyle={"b-reg"}>Total Harga Barang</Text>

              <SkeletonText
                isLoaded={loaded}
                noOfLines={loaded ? undefined : 1}
              >
                <Text textStyle={"b-reg"}>{`Rp ${
                  data && formatNumber(data?.summary.subtotal)
                }`}</Text>
              </SkeletonText>
            </HStack>

            <HStack justify={"space-between"}>
              <Text textStyle={"b-reg"}>Total Ongkos Kirim</Text>
              <SkeletonText
                isLoaded={loaded}
                noOfLines={loaded ? undefined : 1}
              >
                <Text textStyle={"b-reg"}>{`Rp ${
                  data && formatNumber(data?.summary.ongkir)
                }`}</Text>
              </SkeletonText>
            </HStack>
          </>
        )}

        {pageType === 1 && (
          <HStack justify={"space-between"}>
            <Text textStyle={"b-reg"}>Jasa Aplikasi</Text>
            <SkeletonText isLoaded={loaded} noOfLines={loaded ? undefined : 1}>
              <Text textStyle={"b-reg"}>{`Rp ${
                data && formatNumber(data?.summary.app_fee)
              }`}</Text>
            </SkeletonText>
          </HStack>
        )}

        {pageType === 1 && (
          <HStack justify={"space-between"}>
            <Text textStyle={"b-reg-sb"}>Total Pembayaran</Text>

            <SkeletonText isLoaded={loaded} noOfLines={loaded ? undefined : 1}>
              <Text textStyle={"b-reg-sb"}>{`Rp ${
                data && formatNumber(data?.summary.total)
              }`}</Text>
            </SkeletonText>
          </HStack>
        )}

        {pageType !== 1 && (
          <HStack justify={"space-between"} alignItems={"center"}>
            <HStack justify={"center"}>
              <Text textStyle={"b-reg-sb"}>
                Total Saldo Didapatkan Dari Tranksasi
              </Text>

              <PrimaryTooltip title="Harga barang + Ongkos kirim" />
            </HStack>

            <SkeletonText isLoaded={loaded} noOfLines={loaded ? undefined : 1}>
              <Text textStyle={"b-reg-sb"}>{`Rp ${
                data &&
                data.summary.total_points &&
                formatNumber(data?.summary.total_points?.toString())
              }`}</Text>
            </SkeletonText>
          </HStack>
        )}
      </VStack>
    </VStack>
  );
};
